import { Store, select } from '@ngrx/store';
import { Observable } from 'rxjs';
import { Injectable } from '@angular/core';
import { map } from 'rxjs/operators';

import { State } from '@app/store/state/state';
import {
  Billing, BillingUser, BillingVm, BillingResponse, FindBillingCities, BillingCity, BillingCreateItem
} from '@billing/entities/billing.payload';
import {
  fetchBillingAction,
  fetchUserBillingsAction,
  createBillingAction,
  setBillingAction,
  deleteBillingAction,
  fetchBillingCitiesAction,
  fetchDefaultBillingAction,
  setDefaultBillingDataAction,
  fetchCityByIpAction,
  createBillingUIAction,
  createBillingSuccessAction,
  setBillingSuccessAction,
  deleteBillingSuccessAction,
  resetBillingModuleAction
} from '@billing/store/actions/billing.actions';
import { getUiBillingState } from '@billing/store/selectors/ui.selector';
import { UiBillingState } from '@billing/store/state/billing.state';
import { billingRootSelector } from '@billing/store/selectors/billing-root.selector';
import { selectAllBillingInfo } from '@billing/store/selectors/billing.selector';
import { billingStaticId, isStaticBillingId } from './mappers/billing-id.mapper';
import { paxRootSelector } from '@pax/store/selectors/pax-root.selector';
import { TravelBookingType } from '@pax/entities/travel-booking-types';
import { BillingFormService } from '@billing/services/billing-form.service';

@Injectable()
export class BillingFacade {

  constructor(
    private store: Store<State>,
    private billingFormService: BillingFormService,
  ) { }

  public createBillingUI$: Observable<boolean> = this.store
    .pipe(
      select(getUiBillingState),
      map((state: UiBillingState) => state.createBilling)
    );

  public lastBillingUI$: Observable<boolean> = this.store
    .pipe(
      select(getUiBillingState),
      map((state: UiBillingState) => state.lastBilling)
    );

  public billingInfo$: Observable<BillingResponse[]> = this.store.pipe(
    select(selectAllBillingInfo)
  );

  public cities$: Observable<BillingCity[]> = this.store.pipe(
    select(billingRootSelector),
    map(state => state.cities)
  );

  public defaultBillingInfo$: Observable<BillingResponse> = this.store.pipe(
    select(billingRootSelector),
    map(state => state.defaultBilling)
  );

  public bookingType$: Observable<TravelBookingType> = this.store.pipe(
    select(paxRootSelector), map(state => state?.ui?.bookingType));

  public hasDefaultBilling$: Observable<boolean> = this.defaultBillingInfo$.pipe(
    map(defaultData => this.billingFormService.hasDefaultBillingInfo(defaultData))
  );
  
  public fetchDefaultBilling(payload: BillingUser): void {
    this.store.dispatch(fetchDefaultBillingAction({ payload }));
  }

  public fetchBilling(payload: Billing): void {
    this.store.dispatch(fetchBillingAction({ payload }));
  }

  public fetchUserBillings(payload: BillingUser): void {
    this.store.dispatch(fetchUserBillingsAction({ payload }));
  }

  public createBilling(payload: BillingCreateItem): void {
    if (payload.createForm) {
      this.store.dispatch(createBillingAction({ payload }));
    } else {
      const billingResponse: BillingResponse = {
        user_id: payload.payload.user_id,
        _id: billingStaticId(),
        billing_info: payload.payload.billing_info
      };
      this.store.dispatch(createBillingSuccessAction({ response: billingResponse }));
    }
  }

  public setBilling(payload: BillingVm): void {
    if (isStaticBillingId(payload.billingId)) {
      this.store.dispatch(setBillingSuccessAction({ response: payload }));
    } else this.store.dispatch(setBillingAction({ payload }));
  }

  public deleteBilling(payload: Billing): void {
    if (isStaticBillingId(payload.billingId)) {
      this.store.dispatch(deleteBillingSuccessAction({ response: payload }));
    } else this.store.dispatch(deleteBillingAction({ payload }));
  }

  public fetchBillingCities(payload: FindBillingCities): void {
    this.store.dispatch(fetchBillingCitiesAction({ payload }));
  }

  public setDefaultBillingData(response: BillingResponse): void {
    this.store.dispatch(setDefaultBillingDataAction({ response }));
  }

  public fetchIpCityByBilling(): void {
    this.store.dispatch(fetchCityByIpAction());
  }

  public createBillingUI(payload: boolean): void {
    this.store.dispatch(createBillingUIAction({ payload }));
  }

  public resetBillingModuleValues(): void {
    this.store.dispatch(resetBillingModuleAction());
  }
}
